/**
 * <size-variant-picker> Custom Element
 *
 * Wraps a size variant option with EU/UK toggle + radio button grid.
 * Automatically initializes via connectedCallback every time the DOM renders.
 * Active size type is kept in a module-level variable so it persists
 * as long as the page is alive, but resets on full page reload.
 */

let _activeVariantSizeType = "eu";

class SizeVariantPicker extends HTMLElement {
  connectedCallback() {
    this._bindSizeTypeToggle();
    this._bindSizeButtons();
    this._applySizeType(_activeVariantSizeType);
  }

  _getActiveSizeType() {
    return _activeVariantSizeType;
  }

  _setActiveSizeType(sizeType) {
    _activeVariantSizeType = sizeType;
  }

  _applySizeType(sizeType) {
    this._setActiveSizeType(sizeType);

    // Update toggle buttons active state
    this.querySelectorAll("[data-size-system]").forEach((btn) => {
      btn.classList.toggle(
        "active",
        btn.getAttribute("data-size-system") === sizeType
      );
    });

    // Update all size button labels (target spans with data attributes inside block-swatch)
    this.querySelectorAll(".block-swatch span[data-eu-label][data-uk-label]").forEach((label) => {
      label.textContent =
        sizeType === "uk"
          ? label.getAttribute("data-uk-label")
          : label.getAttribute("data-eu-label");
    });

    // Update the main size label (e.g. "Size: UK 7,EU 41" -> "Size: 41" or "Size: 7")
    this._updateSizeLabel(sizeType);
  }

  _bindSizeTypeToggle() {
    this.querySelectorAll("[data-size-system]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._applySizeType(btn.getAttribute("data-size-system"));
      });
    });
  }

  _bindSizeButtons() {
    // Listen for size button changes to update the label
    this.querySelectorAll('input[type="radio"]').forEach((radio) => {
      radio.addEventListener('change', () => {
        this._updateSizeLabelFromSelected();
      });
    });
  }

  _updateSizeLabel(sizeType) {
    const fieldset = this.closest('.variant-picker__option') || this.closest('fieldset');
    const mainSizeLabel = fieldset?.querySelector('[data-size-label]');
    if (!mainSizeLabel) return;

    mainSizeLabel.textContent =
      sizeType === "uk"
        ? mainSizeLabel.getAttribute("data-uk-label")
        : mainSizeLabel.getAttribute("data-eu-label");
  }

  _updateSizeLabelFromSelected() {
    const selectedRadio = this.querySelector('input[type="radio"]:checked');
    if (!selectedRadio) return;

    const labelSpan = selectedRadio.closest('.block-swatch')?.querySelector('span[data-eu-label][data-uk-label]');
    if (!labelSpan) return;

    const sizeType = this._getActiveSizeType();
    const euVal = labelSpan.getAttribute('data-eu-label');
    const ukVal = labelSpan.getAttribute('data-uk-label');

    const fieldset = this.closest('.variant-picker__option') || this.closest('fieldset');
    const mainSizeLabel = fieldset?.querySelector('[data-size-label]');
    if (!mainSizeLabel) return;

    mainSizeLabel.setAttribute('data-eu-label', euVal);
    mainSizeLabel.setAttribute('data-uk-label', ukVal);
    mainSizeLabel.textContent = sizeType === "uk" ? ukVal : euVal;
  }
}

if (!window.customElements.get("size-variant-picker")) {
  window.customElements.define("size-variant-picker", SizeVariantPicker);
}
